"use client";

import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import LocalizedLink from "@/components/LocalizedLink";
import { useReducedMotion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import {
  ArrowRight,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  Maximize2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/cta-button";
import ImageModal from "@/components/image-modal";
import { Project, ProjectMedia, ProjectTechStackItem } from "@/lib/schema";
import { useCurrentLocale } from "@/lib/useCurrentLocale";
import { getMessages } from "@/lib/messages";
import { buildersOf, healthcareLang, HEALTHCARE_TEAM_COPY } from "@/lib/healthcare-team";

export interface ProjectWithMedia extends Project {
  media?: ProjectMedia[];
}

interface WorkDetailsClientProps {
  project: ProjectWithMedia;
  relatedProjects?: Project[];
}

export function MarkdownText({
  content,
  className = "",
}: {
  content?: string | null;
  className?: string;
}) {
  if (!content) return null;
  return (
    <div className={`prose dark:prose-invert max-w-none text-inherit leading-relaxed ${className}`}>
      <ReactMarkdown
        components={{
          p: ({ node, ...props }) => (
            <p {...props} className="mb-4 last:mb-0 text-inherit leading-relaxed" />
          ),
          a: ({ node, href, children, ...props }) => {
            const isExternal =
              href?.startsWith("http://") || href?.startsWith("https://");
            // Internal links in CMS copy are often written as /en-us/... or as
            // absolute https://www.woltrio.com/en-us/... URLs; LocalizedLink
            // rewrites them to the reader's locale (and keeps them same-tab).
            const ownSite = href?.match(/^https?:\/\/(?:www\.)?woltrio\.com(\/[^?#]*)?/i);
            const internalPath = href?.startsWith("/") ? href : ownSite ? ownSite[1] || "/" : null;
            if (internalPath) {
              return (
                <LocalizedLink
                  href={internalPath}
                  className="text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 underline underline-offset-4 font-semibold transition-colors inline"
                >
                  {children}
                </LocalizedLink>
              );
            }
            return (
              <a
                href={href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                className="text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 underline underline-offset-4 font-semibold transition-colors inline"
                {...props}
              >
                {children}
              </a>
            );
          },
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc list-outside ml-5 space-y-2 my-3 text-inherit" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal list-outside ml-5 space-y-2 my-3 text-inherit" />
          ),
          li: ({ node, ...props }) => (
            <li {...props} className="text-inherit leading-relaxed pl-1" />
          ),
          strong: ({ node, ...props }) => (
            <strong {...props} className="font-semibold text-foreground" />
          ),
          em: ({ node, ...props }) => (
            <em {...props} className="italic text-foreground/90" />
          ),
          code: ({ node, ...props }) => (
            <code
              {...props}
              className="px-1.5 py-0.5 rounded bg-muted text-xs font-mono text-foreground border border-border"
            />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote
              {...props}
              className="border-l-2 border-orange-500 pl-4 italic text-foreground/90 my-4"
            />
          ),
          // Markdown "#" inside a case study must not create a second <h1>.
          h1: ({ node, ...props }) => (
            <h2
              {...props}
              className="text-2xl font-semibold font-display text-foreground mt-6 mb-3"
            />
          ),
          h2: ({ node, ...props }) => (
            <h2
              {...props}
              className="text-xl font-semibold font-display text-foreground mt-5 mb-2.5"
            />
          ),
          h3: ({ node, ...props }) => (
            <h3
              {...props}
              className="text-lg font-semibold font-display text-foreground mt-4 mb-2"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}

// In-content interactive screenshot gallery with clean minimal hairline borders
function InContentGallery({
  projectTitle,
  media,
}: {
  projectTitle: string;
  media?: ProjectMedia[];
}) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!media || media.length === 0) return null;

  const currentItem = media[activeIndex] || media[0];

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className="my-8 border border-border bg-card/20 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-[11px] font-mono font-medium uppercase tracking-widest text-orange-500">
            UI / UX Showcases
          </span>
          <h4 className="font-display font-semibold text-base sm:text-lg text-foreground">
            {projectTitle} — Screen Overview
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrev}
            className="p-1.5 rounded bg-background hover:bg-muted border border-border text-foreground transition-colors"
            aria-label="Previous screen"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-xs font-mono text-muted-foreground px-2 py-0.5">
            {activeIndex + 1} / {media.length}
          </span>
          <button
            type="button"
            onClick={handleNext}
            className="p-1.5 rounded bg-background hover:bg-muted border border-border text-foreground transition-colors"
            aria-label="Next screen"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Main Image Viewport */}
      <div className="relative w-full h-[360px] sm:h-[440px] md:h-[480px] bg-background/50 border border-border/80 flex items-center justify-center overflow-hidden group">
        <div className="relative w-full h-full flex items-center justify-center p-4">
          <ImageModal
            src={currentItem.url}
            alt={`${projectTitle} screenshot ${activeIndex + 1}`}
            className="max-h-full max-w-full w-auto h-auto object-contain cursor-zoom-in"
          />
        </div>
        <div className="absolute top-3 right-3 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="px-2.5 py-1 rounded bg-black/75 text-[11px] font-medium text-white flex items-center gap-1">
            <Maximize2 size={12} className="text-orange-400" />
            Zoom
          </span>
        </div>
      </div>

      {/* Minimal thumbnail strip */}
      {media.length > 1 && (
        <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {media.map((item, idx) => {
            const isSelected = idx === activeIndex;
            return (
              <button
                key={item.id || idx}
                type="button"
                onClick={() => setActiveIndex(idx)}
                className={cn(
                  "relative shrink-0 overflow-hidden transition-all duration-150 focus-visible:outline-none",
                  isSelected
                    ? "ring-1 ring-orange-500 opacity-100"
                    : "opacity-40 hover:opacity-100 border border-border"
                )}
                aria-label={`Select screenshot ${idx + 1}`}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-background p-1 flex items-center justify-center">
                  <Image
                    src={item.url}
                    alt={`Thumbnail ${idx + 1}`}
                    width={80}
                    height={80}
                    className="w-full h-full object-contain"
                  />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** "clutch.co" from a review URL, for "Read on clutch.co" labels. */
function reviewHost(url: string) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "the review site";
  }
}

/** "2026-03" → "March 2026" in the page's locale; null when unset or malformed. */
function formatCompleted(ym: string | null | undefined, locale: string) {
  const m = ym?.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const date = new Date(Number(m[1]), Number(m[2]) - 1, 1);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
}

const TOC_SECTIONS = [
  { id: "client-overview", label: "Client overview" },
  { id: "challenge", label: "Challenge" },
  { id: "solution", label: "Solution" },
  { id: "technologies", label: "Technologies" },
  { id: "team", label: "Team" },
  { id: "results", label: "Results" },
];

export default function WorkDetailsClient({
  project,
  relatedProjects = [],
}: WorkDetailsClientProps) {
  const locale = useCurrentLocale();
  const t = getMessages(locale);
  const prefersReduced = useReducedMotion();
  const completedLabel = formatCompleted(project.completedAt, locale);
  const builders = buildersOf(project.slug);
  const builtByLabel = HEALTHCARE_TEAM_COPY[healthcareLang(locale)].builtBy;

  const [activeSection, setActiveSection] = useState<string>("client-overview");

  // Scrollspy to update active TOC item on scroll
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 180;
      for (const section of TOC_SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  // Group tech stack
  const groupedTechStack = useMemo(() => {
    if (!project.techStack || !Array.isArray(project.techStack) || project.techStack.length === 0) {
      if (project.tags && project.tags.length > 0) {
        return [
          {
            category: "Core Stack & Technologies",
            items: project.tags.map((tag) => ({
              name: tag,
              description: `Production deployment & integration for ${project.title}`,
            })),
          },
        ];
      }
      return [
        {
          category: "Modern Cloud Architecture",
          items: [
            { name: "TypeScript & Next.js", description: "Type-safe high-performance frontend" },
            { name: "Node.js & Cloud APIs", description: "Scalable backend architecture" },
            { name: "PostgreSQL Database", description: "Relational persistence & optimized queries" },
            { name: "Docker & Cloud DevOps", description: "Continuous integration & deployment" },
          ],
        },
      ];
    }

    const map = new Map<string, ProjectTechStackItem[]>();
    for (const item of project.techStack) {
      const category = item.category || "Core Technologies";
      if (!map.has(category)) {
        map.set(category, []);
      }
      map.get(category)!.push(item);
    }
    return Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [project.techStack, project.tags, project.title]);

  // Team composition dynamically loaded from database
  const teamMembers = useMemo(() => {
    if (project.teamPod && Array.isArray(project.teamPod) && project.teamPod.length > 0) {
      return project.teamPod.map((item) => ({
        role: item.role,
        count: item.count || 1,
        description: item.description || "Dedicated Woltrio Pod Member",
      }));
    }
    return [
      { role: "Enterprise Solution Architect", count: 1, description: "System architecture & technical strategy" },
      { role: "Lead Full-Stack Engineer", count: 1, description: "Core product development & cloud backend" },
      { role: "Senior Frontend Engineer", count: 1, description: "Modern UI/UX implementation & component design" },
      { role: "UI/UX Product Designer", count: 1, description: "Design systems & interactive prototypes" },
      { role: "Lead QA Engineer", count: 1, description: "Automated test suites & quality assurance" },
    ];
  }, [project.teamPod]);

  return (
    <main className="bg-background text-foreground min-h-screen relative selection:bg-orange-500/20 selection:text-orange-500">
      {/* ─────────────────────────────────────────────────────────
          1. HERO HEADER WITH METADATA SIDEBAR
      ───────────────────────────────────────────────────────── */}
      <section className="relative pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 lg:px-12 border-b border-border">
        <div className="mx-auto max-w-[88rem]">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center space-x-2 text-xs uppercase tracking-[0.2em] text-foreground/50">
              <li>
                <LocalizedLink href="/" className="hover:text-orange-500 transition-colors">
                  Home
                </LocalizedLink>
              </li>
              <li>/</li>
              <li>
                <LocalizedLink href="/case-studies" className="hover:text-orange-500 transition-colors">
                  Case Studies
                </LocalizedLink>
              </li>
              <li>/</li>
              <li className="text-foreground/80 truncate max-w-[200px] sm:max-w-md">
                {project.title}
              </li>
            </ol>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left Column: Title, Subtitle, Highlights */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-orange-500">
                  {project.categories?.[0] || "Case Study"}
                </span>
                {project.clientName && (
                  <>
                    <span className="text-foreground/40">·</span>
                    <span className="text-xs font-mono uppercase tracking-[0.15em] text-foreground/60">
                      Built for {project.clientName}
                    </span>
                  </>
                )}
                {project.headquarters && (
                  <>
                    <span className="text-foreground/40">·</span>
                    <span className="text-xs font-mono uppercase tracking-[0.15em] text-foreground/60">
                      {project.headquarters}
                    </span>
                  </>
                )}
              </div>

              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-[-0.02em] leading-[1.08] text-foreground">
                {project.title}
              </h1>

              <p className="text-base sm:text-lg leading-relaxed text-foreground/80">
                {project.description}
              </p>

              {/* Highlight banner with dynamic delivery timeline */}
              {project.deliveryTimeline && (
                <div className="pt-2 border-t border-border">
                  <div className="flex items-baseline gap-2.5">
                    <div className="font-display font-semibold text-3xl sm:text-4xl text-orange-500">
                      {project.deliveryTimeline.replace(/\s*\(.*\)/, "")}
                    </div>
                    {project.deliveryTimeline.includes("(") && (
                      <span className="text-xs font-mono uppercase tracking-wider text-orange-500">
                        {project.deliveryTimeline.match(/\((.*?)\)/)?.[0]}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-foreground/60 mt-1">
                    were needed to design, engineer & launch the full platform
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Button href="/contact" variant="primary">
                  Start my project
                </Button>

                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground/70 hover:text-orange-500 transition-colors"
                  >
                    <span>View Live</span>
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>

            {/* Right Column: Hero Cover & Structured Metadata Table */}
            <div className="lg:col-span-5 space-y-6">
              <div className="relative w-full h-56 sm:h-64 border border-border bg-background/50 overflow-hidden">
                <Image
                  src={project.imageUrl || "/assets/linkwell.png"}
                  alt={project.title}
                  fill
                  priority
                  className="object-cover"
                />
              </div>

              {/* Dynamic Metadata List with Hairline Dividers */}
              <div className="border-t border-border divide-y divide-border text-sm">
                {project.clientName && (
                  <div className="py-3 flex items-center justify-between gap-4">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Client</span>
                    <span className="font-medium text-foreground text-right">{project.clientName}</span>
                  </div>
                )}
                {completedLabel && (
                  <div className="py-3 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Completed</span>
                    <span className="font-medium text-foreground">
                      <time dateTime={project.completedAt ?? undefined}>{completedLabel}</time>
                    </span>
                  </div>
                )}
                {builders.length > 0 && (
                  <div className="py-3 flex items-center justify-between gap-4">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">{builtByLabel}</span>
                    <span className="font-medium text-foreground text-right">
                      {builders.map((b, i) => (
                        <React.Fragment key={b.id}>
                          {i > 0 && ", "}
                          <LocalizedLink
                            href={`/team#${b.id}`}
                            className="underline decoration-foreground/25 underline-offset-4 hover:text-orange-500 hover:decoration-orange-500"
                          >
                            {b.name}
                          </LocalizedLink>
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                )}
                <div className="py-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-foreground/50">Industry</span>
                  <span className="font-medium text-foreground">{project.industry || "Enterprise Software"}</span>
                </div>
                <div className="py-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-foreground/50">Region</span>
                  <span className="font-medium text-foreground">{project.headquarters || "International"}</span>
                </div>
                <div className="py-3 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-wider text-foreground/50">Platform</span>
                  <span className="font-medium text-foreground">{project.platform || "Web & Cloud Platform"}</span>
                </div>
                {project.deliveryTimeline && (
                  <div className="py-3 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Delivery</span>
                    <span className="font-medium text-foreground">{project.deliveryTimeline}</span>
                  </div>
                )}
                {project.estimatedBudget && (
                  <div className="py-3 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Estimated Budget</span>
                    <span className="font-medium text-foreground">{project.estimatedBudget}</span>
                  </div>
                )}
                {project.servicesProvided && (
                  <div className="py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Services</span>
                    <span className="font-medium text-foreground text-right text-xs sm:text-sm">
                      {project.servicesProvided}
                    </span>
                  </div>
                )}
                {/* Only when there is a public review to back it; a fixed
                    "5.0 / 5.0" used to print on every project, reviewed or not. */}
                {project.reviewUrl && (
                  <div className="py-3 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-wider text-foreground/50">Client review</span>
                    <a
                      href={project.reviewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 font-medium text-orange-500 hover:underline"
                    >
                      Read on {reviewHost(project.reviewUrl)}
                      <ExternalLink size={14} aria-hidden="true" />
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          2. MAIN CONTENT BODY + STICKY SIDEBAR (TOC + CTA)
      ───────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-24 px-5 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-[88rem]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* ─────────────────────────────────────────────────────
                MAIN ARTICLE COLUMN (LEFT, 8 COLS)
            ───────────────────────────────────────────────────── */}
            <div className="lg:col-span-8 space-y-16 md:space-y-20">

              {/* ─────────────────────────────────────────
                  SECTION 1: CLIENT OVERVIEW
              ───────────────────────────────────────── */}
              <section id="client-overview" className="scroll-mt-32 space-y-6">
                <div className="border-b border-border pb-4 flex items-center justify-between">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Client overview
                  </h2>
                  <span className="text-xs font-mono uppercase tracking-widest text-foreground/40">
                    01 / 06
                  </span>
                </div>

                <div className="text-base sm:text-lg text-foreground/80 leading-relaxed space-y-4">
                  <MarkdownText content={project.fullDescription || project.description} />
                </div>

                {/* Scope items / What We Did / Audience cards if available */}
                {project.whatWeDid && project.whatWeDid.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-4 border-t border-border">
                    {project.whatWeDid.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="space-y-1.5">
                        <div className="text-xs font-mono uppercase tracking-wider text-orange-500">
                          {item.title}
                        </div>
                        <div className="text-sm text-foreground/70 leading-relaxed">
                          {item.description}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : project.tags && project.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-muted/60 text-foreground/75 rounded-lg text-xs font-medium border border-border/40"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                ) : null}
              </section>

              {/* ─────────────────────────────────────────
                  SECTION 2: CHALLENGE
              ───────────────────────────────────────── */}
              <section id="challenge" className="scroll-mt-32 space-y-8">
                <div className="border-b border-border pb-4 flex items-center justify-between">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Challenge
                  </h2>
                  <span className="text-xs font-mono uppercase tracking-widest text-foreground/40">
                    02 / 06
                  </span>
                </div>

                <div className="text-base sm:text-lg text-foreground/80 leading-relaxed space-y-4">
                  <MarkdownText content={project.challenge} />
                </div>

                {/* Issues & Resolutions breakdown if available */}
                {project.issuesAndResolutions && project.issuesAndResolutions.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-border pt-8">
                    {project.issuesAndResolutions.map((item, idx) => (
                      <div key={idx} className="space-y-2">
                        <div className="text-xs font-mono uppercase tracking-wider text-foreground/60">
                          {item.issue}
                        </div>
                        <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                          {item.solution}
                        </p>
                        {item.outcome && (
                          <div className="text-xs text-orange-500 font-mono mt-1">
                            → {item.outcome}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Testimonial Quote Statement */}
                {project.testimonialQuote && (
                  <div className="border-t border-border pt-8 mt-6">
                    <blockquote
                      className="font-display font-semibold italic text-foreground leading-[1.25] tracking-[-0.01em]"
                      style={{ fontSize: "clamp(1.25rem, 2.5vw, 1.75rem)" }}
                    >
                      <span className="text-orange-500">&ldquo;</span>
                      {project.testimonialQuote}
                      <span className="text-orange-500">&rdquo;</span>
                    </blockquote>
                    <p className="mt-4 text-sm text-foreground/60">
                      {/* Author is the person; role is their title and organisation.
                          No invented fallback — an unattributed quote shows as such. */}
                      {project.testimonialAuthor && (
                        <span className="font-semibold text-foreground/80">{project.testimonialAuthor}</span>
                      )}
                      {project.testimonialAuthor && project.testimonialRole ? " · " : ""}
                      {project.testimonialRole}
                      {project.reviewUrl && (
                        <>
                          {" · "}
                          <a
                            href={project.reviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline decoration-foreground/25 underline-offset-4 hover:text-orange-500 hover:decoration-orange-500"
                          >
                            Verified review on {reviewHost(project.reviewUrl)}
                          </a>
                        </>
                      )}
                    </p>
                  </div>
                )}
              </section>

              {/* ─────────────────────────────────────────
                  SECTION 3: SOLUTION
              ───────────────────────────────────────── */}
              <section id="solution" className="scroll-mt-32 space-y-8">
                <div className="border-b border-border pb-4 flex items-center justify-between">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Solution we delivered
                  </h2>
                  <span className="text-xs font-mono uppercase tracking-widest text-foreground/40">
                    03 / 06
                  </span>
                </div>

                <div className="text-base sm:text-lg text-foreground/80 leading-relaxed space-y-4">
                  <MarkdownText content={project.solution} />
                </div>

                {/* IN-CONTENT GALLERY */}
                <InContentGallery
                  projectTitle={project.title}
                  media={project.media}
                />

                {/* Key features breakdown if present */}
                {project.keyFeatures && project.keyFeatures.length > 0 ? (
                  <div className="space-y-6 border-t border-border pt-6">
                    {project.keyFeatures.map((feat, idx) => (
                      <div key={idx} className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground">
                            {idx + 1}. {feat.title}
                          </h3>
                          {feat.tag && (
                            <span className="px-2 py-0.5 rounded text-[11px] font-mono uppercase bg-orange-500/10 text-orange-500">
                              {feat.tag}
                            </span>
                          )}
                        </div>
                        <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                          {feat.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : project.howWeDidIt && project.howWeDidIt.length > 0 ? (
                  <div className="space-y-6 border-t border-border pt-6">
                    {project.howWeDidIt.map((step, idx) => (
                      <div key={idx} className="space-y-1.5">
                        <h3 className="font-display text-lg sm:text-xl font-semibold text-foreground">
                          {step.stepNumber}. {step.title}
                        </h3>
                        <p className="text-sm sm:text-base text-foreground/80 leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}

                {/* Custom rich sections if present */}
                {project.customSections && project.customSections.length > 0 && (
                  <div className="space-y-6 border-t border-border pt-6">
                    {project.customSections.map((sec, idx) => (
                      <div key={idx} className="space-y-2 p-5 rounded-2xl bg-muted/20 border border-border">
                        <h3 className="font-display text-lg font-semibold text-foreground">
                          {sec.title}
                        </h3>
                        {sec.subtitle && (
                          <p className="text-xs font-mono uppercase text-foreground/60">{sec.subtitle}</p>
                        )}
                        <MarkdownText content={sec.content} className="text-sm text-foreground/80" />
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* ─────────────────────────────────────────
                  SECTION 4: TECHNOLOGIES
              ───────────────────────────────────────── */}
              <section id="technologies" className="scroll-mt-32 space-y-8">
                <div className="border-b border-border pb-4 flex items-center justify-between">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Technologies
                  </h2>
                  <span className="text-xs font-mono uppercase tracking-widest text-foreground/40">
                    04 / 06
                  </span>
                </div>

                <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">
                  The technology stack was selected for rapid development velocity, high reliability, robust security, and seamless horizontal scalability.
                </p>

                {/* Categorized Tech Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 border-t border-border pt-8">
                  {groupedTechStack.map((group, idx) => (
                    <div key={idx} className="space-y-4">
                      <div className="text-xs font-mono uppercase tracking-wider text-orange-500">
                        {group.category}
                      </div>

                      <div className="space-y-3 divide-y divide-border/40">
                        {group.items.map((tech, tIdx) => (
                          <div key={tIdx} className="pt-2.5 first:pt-0">
                            <div className="font-semibold text-sm text-foreground">
                              {tech.name}
                            </div>
                            {tech.description && (
                              <div className="text-xs text-foreground/60 mt-0.5 leading-relaxed">
                                {tech.description}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* ─────────────────────────────────────────
                  SECTION 5: TEAM
              ───────────────────────────────────────── */}
              <section id="team" className="scroll-mt-32 space-y-8">
                <div className="border-b border-border pb-4 flex items-center justify-between">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Team
                  </h2>
                  <span className="text-xs font-mono uppercase tracking-widest text-foreground/40">
                    05 / 06
                  </span>
                </div>

                <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">
                  A dedicated, cross-functional agile engineering pod worked in lockstep with the client through weekly sprints, daily asynchronous check-ins, and continuous delivery.
                </p>

                {/* Team Breakdown List */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 border-t border-border pt-8">
                  {teamMembers.map((member, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="text-xs font-mono font-medium text-orange-500">
                        0{member.count}
                      </div>
                      <div className="font-display font-semibold text-base text-foreground">
                        {member.role}
                      </div>
                      <div className="text-xs text-foreground/60">
                        {member.description}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-border pt-4 text-xs sm:text-sm text-foreground/70 leading-relaxed">
                  <strong className="text-foreground font-semibold">Agile Delivery Framework: </strong>
                  Bi-weekly sprint planning, continuous automated testing, and direct collaborative alignment ensured zero blockers throughout the {project.deliveryTimeline || "project"} cycle.
                </div>
              </section>

              {/* ─────────────────────────────────────────
                  SECTION 6: RESULTS
              ───────────────────────────────────────── */}
              <section id="results" className="scroll-mt-32 space-y-8">
                <div className="border-b border-border pb-4 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.02em] text-foreground">
                    Results
                  </h2>
                  {project.deliveryTimeline && (
                    <div className="text-xs font-mono uppercase tracking-widest text-foreground/50">
                      Project duration: <strong className="text-foreground">{project.deliveryTimeline}</strong>
                    </div>
                  )}
                </div>

                {/* Metric Scorecards */}
                {project.stats && project.stats.length > 0 && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 border-t border-border pt-8">
                    {project.stats.map((stat, idx) => (
                      <div key={idx}>
                        <div className="font-display font-bold text-4xl sm:text-5xl text-orange-500 leading-none">
                          {stat.value}
                        </div>
                        <div className="font-semibold text-sm text-foreground mt-2">
                          {stat.label}
                        </div>
                        {stat.description && (
                          <div className="text-xs text-foreground/60 mt-1">
                            {stat.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Narrative Summary */}
                <div className="text-base sm:text-lg text-foreground/80 leading-relaxed space-y-4 border-t border-border pt-8">
                  <p>
                    Woltrio successfully delivered {project.title} on schedule
                    {project.deliveryTimeline ? ` within the agreed ${project.deliveryTimeline} timeline` : ""}.
                  </p>
                  <p>
                    The deployed platform achieved exceptional operational performance, mission-critical reliability, and seamless user adoption across all target workflows.
                  </p>
                </div>
              </section>

            </div>

            {/* ─────────────────────────────────────────────────────
                STICKY SIDEBAR (RIGHT, 4 COLS)
            ───────────────────────────────────────────────────── */}
            <aside className="lg:col-span-4 sticky top-28 space-y-8">
              
              {/* Minimal Table of Contents */}
              <div className="border-t border-border pt-4 space-y-3">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-foreground/50">
                  Table of contents
                </div>

                <nav className="space-y-0.5">
                  {TOC_SECTIONS.map((sec, idx) => {
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        type="button"
                        onClick={() => scrollToSection(sec.id)}
                        className={cn(
                          "w-full text-left py-2 text-sm transition-colors duration-150 flex items-center justify-between group",
                          isActive
                            ? "text-orange-500 font-semibold pl-2 border-l-2 border-orange-500"
                            : "text-foreground/60 hover:text-foreground pl-2 border-l-2 border-transparent"
                        )}
                      >
                        <span>{sec.label}</span>
                        <span className="text-[11px] font-mono text-foreground/30">
                          0{idx + 1}
                        </span>
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Minimal Sidebar CTA */}
              <div className="border-t border-border pt-6 space-y-3">
                <div className="text-xs font-mono uppercase tracking-wider text-orange-500">
                  Let&apos;s Build Together
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground leading-snug">
                  Build your next {project.categories?.[0] ? project.categories[0].toLowerCase() : "high-growth"} platform with Woltrio
                </h3>
                <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed">
                  From discovery to production launch, our dedicated engineering teams build robust, scalable digital products that drive results.
                </p>
                <div className="pt-2">
                  <Button href="/contact" variant="primary" className="w-full">
                    Let&apos;s talk
                  </Button>
                </div>
              </div>

            </aside>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          3. RELATED CASES (Hairline Row Links)
      ───────────────────────────────────────────────────────── */}
      {relatedProjects && relatedProjects.length > 0 && (
        <section className="py-20 px-5 sm:px-8 lg:px-12 border-t border-border">
          <div className="mx-auto max-w-[88rem]">
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs font-mono uppercase tracking-[0.22em] text-foreground/50">
                Related Case Studies
              </p>
              <LocalizedLink
                href="/case-studies"
                className="text-xs font-mono uppercase tracking-wider text-orange-500 hover:text-orange-600 flex items-center gap-1"
              >
                <span>View all projects</span>
                <ArrowRight size={13} />
              </LocalizedLink>
            </div>

            <div className="divide-y divide-border border-y border-border">
              {relatedProjects.slice(0, 4).map((rel) => (
                <LocalizedLink
                  key={rel.id}
                  href={`/case-studies/${rel.slug}`}
                  className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
                >
                  <div className="space-y-1">
                    <div className="text-xs font-mono uppercase tracking-wider text-orange-500">
                      {rel.categories?.[0] || "Case Study"}
                    </div>
                    <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-orange-500 transition-colors">
                      {rel.title}
                    </h3>
                    <p className="text-sm text-foreground/70 max-w-2xl line-clamp-1">
                      {rel.description}
                    </p>
                  </div>
                  <ArrowRight
                    size={16}
                    className="flex-shrink-0 text-foreground/40 group-hover:text-orange-500 group-hover:translate-x-1 transition-all duration-200"
                  />
                </LocalizedLink>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─────────────────────────────────────────────────────────
          4. NEED OTHER SERVICES? (Minimal Hairline List)
      ───────────────────────────────────────────────────────── */}
      <section className="py-16 md:py-20 px-5 sm:px-8 lg:px-12 border-t border-border">
        <div className="mx-auto max-w-[88rem]">
          <p className="text-xs font-mono uppercase tracking-[0.22em] text-foreground/50 mb-8">
            Capabilities & Services
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 border-t border-border pt-8">
            <LocalizedLink href="/services" className="group space-y-1.5">
              <div className="font-display font-semibold text-base text-foreground group-hover:text-orange-500 transition-colors">
                Mobile App Development
              </div>
              <p className="text-xs sm:text-sm text-foreground/60 leading-relaxed">
                Native iOS & Android and React Native applications built for performance.
              </p>
            </LocalizedLink>

            <LocalizedLink href="/services" className="group space-y-1.5">
              <div className="font-display font-semibold text-base text-foreground group-hover:text-orange-500 transition-colors">
                Custom Web Development
              </div>
              <p className="text-xs sm:text-sm text-foreground/60 leading-relaxed">
                High-throughput web applications, portals, and SaaS platforms.
              </p>
            </LocalizedLink>

            <LocalizedLink href="/services" className="group space-y-1.5">
              <div className="font-display font-semibold text-base text-foreground group-hover:text-orange-500 transition-colors">
                MVP Development for Startups
              </div>
              <p className="text-xs sm:text-sm text-foreground/60 leading-relaxed">
                Rapid concept-to-launch agile delivery in 6 to 8 weeks.
              </p>
            </LocalizedLink>

            <LocalizedLink href="/services" className="group space-y-1.5">
              <div className="font-display font-semibold text-base text-foreground group-hover:text-orange-500 transition-colors">
                AI & Workflow Automation
              </div>
              <p className="text-xs sm:text-sm text-foreground/60 leading-relaxed">
                Custom machine learning models, LLM pipelines, and automated intelligence.
              </p>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────────
          5. CLOSING SERVICE CTA BAND (Standard Woltrio band)
      ───────────────────────────────────────────────────────── */}
      <section className="relative z-10 border-t border-border bg-transparent px-5 py-24 text-foreground sm:px-8 md:py-36 lg:px-12">
        <div className="mx-auto flex max-w-[88rem] flex-col items-start justify-between gap-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <h2 className="font-display text-balance text-3xl font-semibold leading-[1.08] tracking-[-0.02em] md:text-5xl">
              Ready to build your next big idea?
            </h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/80 md:text-lg">
              Join our portfolio of successful partners and transform your vision into reality.
            </p>
          </div>
          <Button href="/contact" variant="primary" size="lg" className="flex-shrink-0">
            START YOUR PROJECT
          </Button>
        </div>
      </section>
    </main>
  );
}
